import { type RoomReservationSummary } from '@exam-center/exam-location-management';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  OutgoingMailOutlinedIcon,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import dayjs from 'dayjs';

import { useSendRevokeNotificationEmail } from '../../hooks/use-send-revoke-notification-email';

interface CannotRevokeExamCenterLocationPermissionModalProps {
  open: boolean;
  onClose: () => void;
  reservation: RoomReservationSummary;
}

export default function CannotRevokeExamCenterLocationPermissionModal(
  props: Readonly<CannotRevokeExamCenterLocationPermissionModalProps>,
) {
  const { open: isOpen, onClose, reservation } = props;

  const { showSnackbar } = useSnackbar();
  const [sendEmail, { loading: isSendingEmail }] = useSendRevokeNotificationEmail();

  const handleSendEmail = async () => {
    await sendEmail(reservation.examCenterCode);
    showSnackbar({ message: 'ส่งอีเมลแจ้งศูนย์ทดสอบเรียบร้อยแล้ว', variant: 'success' });
    onClose();
  };

  const startDateFormat = dayjs(reservation.startDate).format('DD/MM/YYYY');
  const endDateFormat = dayjs(reservation.endDate).format('DD/MM/YYYY');
  const usageDateRange = `${startDateFormat} - ${endDateFormat}`;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle onClose={onClose}>ไม่สามารถยกเลิกสิทธิ์การใช้งานได้</DialogTitle>
      <DialogContent dividers>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(3),
          })}
        >
          <Text>
            ระบบตรวจพบว่า มีรอบสอบภายในศูนย์ทดสอบที่ยังใช้งานสถานที่นี้อยู่ กรุณาแจ้งศูนย์ทดสอบ
            ให้แก้ไขสถานที่สอบออกจากรอบสอบทั้งหมดก่อน
          </Text>

          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ width: '110px', flexShrink: 0 }}>
              ศูนย์ทดสอบ:
            </Text>
            <Text>{reservation.examCenterCode}</Text>
          </Flex>

          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ width: '110px', flexShrink: 0 }}>
              จำนวนรอบสอบ:
            </Text>
            <Text>{reservation.eventCount} รอบ</Text>
          </Flex>

          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ width: '110px', flexShrink: 0 }}>
              ช่วงวันที่ใช้งาน:
            </Text>
            <Text>{usageDateRange}</Text>
          </Flex>

          <Text>ระบบจะอนุญาตให้ยกเลิกได้ เมื่อไม่มีรอบสอบใดใช้งานสถานที่นี้อยู่แล้ว</Text>
        </Flex>
      </DialogContent>

      <Divider />
      <DialogActions
        sx={(theme) => ({
          justifyContent: 'space-between',
          p: theme.spacing(5, 6, 6, 6),
        })}
      >
        <Button
          size='large'
          variant='outlined'
          color='secondary'
          sx={{
            visibility: 'hidden',
          }}
          onClick={handleSendEmail}
          loading={isSendingEmail}
          startIcon={<OutgoingMailOutlinedIcon />}
        >
          ส่งอีเมลแจ้งศูนย์ทดสอบให้แก้ไข
        </Button>
        <Button size='large' color='primary' onClick={onClose} disabled={isSendingEmail}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
